import * as React from 'react'
import {Component, createRef, RefObject} from 'react'
import {createPortal} from 'react-dom'
import {inject, observer} from 'mobx-react'
import disabled from 'ally.js/maintain/disabled'
import tabFocus from 'ally.js/maintain/tab-focus'
import firstTabbable from 'ally.js/query/first-tabbable'
import {WidgetApi} from './WidgetApi'
import {OverlayMode} from './OverlayMode'
import {WidgetOverlayFlags} from './WidgetFlag'
import {getElementDomPath} from './helpers'
import {IconClose} from '../components/ui/Icons'

///////////////////////////////////////////////////////////////////////////////

export type OverlayBlockWrapperProps = {
  api?: WidgetApi
  children?: any
  showHeader?: boolean
  showClose?: boolean
  title?: string
  closeLabel?: string
  onClose?: () => void
  closeOnClickOutside?: boolean
  closeOnEsc?: boolean
  className?: string
}

interface Handle {
  disengage(): void
}

///////////////////////////////////////////////////////////////////////////////

@inject('api')
@observer
export class OverlayBlockWrapper extends Component<
  OverlayBlockWrapperProps,
  any
> {
  private bodyClassName: string
  private wrapper: RefObject<HTMLDivElement>
  private dialog: RefObject<HTMLDivElement>
  private portal: HTMLDivElement

  private focusedElementDomPathBeforeOpen: string
  private disabledHandle: Handle
  private focusHandle: Handle

  public static defaultProps: OverlayBlockWrapperProps = {
    showHeader: true,
    showClose: true,
    closeOnClickOutside: true,
    closeOnEsc: true,
  }

  constructor(props) {
    super(props)

    this.setup()
  }

  setup() {
    this.bodyClassName = 'tamaro-widget-overlay-shown'
    this.wrapper = createRef()
    this.dialog = createRef()

    this.onClick = this.onClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    this.focusedElementDomPathBeforeOpen = getElementDomPath(
      document.activeElement,
    )

    if (this.showInPortal()) {
      this.portal = document.body.querySelector('#tamaro-widget-overlay')

      if (!this.portal) {
        this.portal = document.createElement('div')
        this.portal.setAttribute('id', 'tamaro-widget-overlay')
        this.portal.classList.add('tamaro-widget')

        document.body.appendChild(this.portal)
      }
    }
  }

  componentDidMount() {
    let {api} = this.props

    document.body.classList.add(this.bodyClassName)

    if (api.computedConfig.overlayMode === OverlayMode.OUTER) {
      document.addEventListener('click', this.onClick)
      document.addEventListener('keydown', this.onKeydown)

      this.disabledHandle = disabled({
        filter: this.dialog.current,
      })

      this.focusHandle = tabFocus({
        context: this.dialog.current,
      })
    }

    if (api.computedConfig.overlayMode === OverlayMode.INNER) {
      setTimeout(async () => {
        await api.ui.scrollToTop()
      })
    }

    let el = firstTabbable({
      context: this.dialog.current,
      defaultToContext: true,
    })

    if (el) {
      el.focus()
    }
  }

  componentWillUnmount() {
    let {api} = this.props

    if (!api.flags.has(WidgetOverlayFlags)) {
      document.body.classList.remove(this.bodyClassName)
    }

    if (api.computedConfig.overlayMode === OverlayMode.OUTER) {
      document.removeEventListener('click', this.onClick)
      document.removeEventListener('keydown', this.onKeydown)

      if (this.disabledHandle) {
        this.disabledHandle.disengage()
      }

      if (this.focusHandle) {
        this.focusHandle.disengage()
      }

      if (this.focusedElementDomPathBeforeOpen) {
        setTimeout(() => {
          let el = document.querySelector(
            this.focusedElementDomPathBeforeOpen,
          ) as HTMLElement

          if (el) {
            el.focus()
          }
        })
      }
    }

    if (this.showInPortal()) {
      this.portal?.parentNode?.removeChild(this.portal)
    }
  }

  showInPortal() {
    let {overlayMode} = this.props.api.computedConfig

    return overlayMode === OverlayMode.OUTER
  }

  renderDialog() {
    let {
      api,
      children,
      showHeader,
      showClose,
      title,
      closeLabel,
      onClose,
      className,
    } = this.props

    return (
      <div className={className}>
        {api.computedConfig.overlayMode === OverlayMode.OUTER && (
          <div className="overlay-backdrop" />
        )}
        <div className="overlay-block-wrapper" ref={this.wrapper}>
          <section
            className="overlay-block"
            ref={this.dialog}
            role="dialog"
            aria-labelledby={api.ui.generateUniqueElementId('overlay-title')}
          >
            {showHeader && (
              <div className="overlay-block-header">
                {title && (
                  <div
                    className="title"
                    id={api.ui.generateUniqueElementId('overlay-title')}
                  >
                    <h2 className="text">{title}</h2>
                  </div>
                )}
                {showClose && onClose && (
                  <button
                    type="button"
                    className="close"
                    onClick={onClose}
                    aria-label={closeLabel}
                    tabIndex={0}
                  >
                    <span className="icon">
                      <IconClose />
                    </span>
                  </button>
                )}
              </div>
            )}
            <div className="overlay-block-content">{children}</div>
          </section>
        </div>
      </div>
    )
  }

  render() {
    if (this.showInPortal() && this.portal) {
      return createPortal(this.renderDialog(), this.portal)
    } else {
      return this.renderDialog()
    }
  }

  onClick(e) {
    let {onClose, closeOnClickOutside} = this.props

    if (onClose && closeOnClickOutside && e.target === this.wrapper.current) {
      onClose()
    }
  }

  onKeydown(e) {
    let {onClose, closeOnEsc} = this.props
    let keyCode = e.which || e.keyCode

    if (onClose && closeOnEsc && keyCode === 27) {
      onClose()
    }
  }
}
